// src/pages/Settings/company/SocialMediaManagement/ChannelsTab.jsx
//
// Task 24873 - "how do we connect wih facebook, X, instagram, etc from here?
// also add a row for this config in settings area too"
// Task 24875 - "for some platforms we need AI to hangel the image na dtext
// generation differnetly. add two fields for AI to use to enhance its logic."
//
// WHAT THIS IS, AND WHAT IT IS NOT
// This is where a company records the accounts it posts to: which platform,
// which handle, where the profile lives, and - from 24875 - the standing
// instructions AI should follow when it writes copy or generates an image for
// that account.
//
// CONNECTING (also 24873)
// Connect runs a real OAuth authorisation: CRMit asks the platform for an
// authorise URL, the operator says yes on the platform's own site, and the
// platform hands back a code that the BACKEND swaps for a token. The token
// never touches the browser - /socialmediaprofile/list names its columns
// precisely so it cannot leak - and the screen works off a FlagConnected the
// server derives.
//
// What it still does not do is PUBLISH. Holding a valid token is one problem;
// posting is a bigger one, different for every platform (Instagram wants a
// media container then a publish call, X wants chunked upload, LinkedIn wants
// a registerUpload handshake). That is Task 20047. The banner below says so,
// because "Connected" must not be read as "your posts will go out".
//
// The rest of the product already reads this list: the compose drawer's
// platform tiles, the calendar's per-post icons, and the two AI endpoints all
// come off SocialMediaProfile. So a channel added here is immediately usable
// for drafting and scheduling, which is most of what the screen is for today.

import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { isEmpty } from "lodash";
import { toast } from "react-toastify";
import { Link } from "react-router-dom";

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import AddIcon from "@mui/icons-material/Add";

import AppModal from "../../../../Components/Common/AppModal";
import DeleteModal from "../../../../Components/Common/DeleteModal";

import {
  fetchMasterSocialMediaProfileList,
  fetchMasterSocialMediaProviderList,
  createMasterSocialMediaProfile,
  createMasterSocialMediaProfileSuccess,
  updateMasterSocialMediaProfile,
  updateMasterSocialMediaProfileSuccess,
  deleteMasterSocialMediaProfile,
  deleteMasterSocialMediaProfileSuccess,
  socialMediaConnectStart,
  socialMediaConnectStartSuccess,
  socialMediaConnectDisconnect,
  socialMediaConnectDisconnectSuccess,
} from "../../../../store/socialMediaManagement/action";

// A blank channel. MasterSocialMediaProvider_Id is the only thing the rest of
// the product genuinely needs (it drives the icon and the platform guidance the
// AI endpoints apply), so it is the only required field.
const EMPTY_CHANNEL = {
  Id: null,
  MasterSocialMediaProvider_Id: "",
  HandleName: "",
  UrlProfileView: "",
  Notes: "",
  AiPromptContentGeneration: "",
  AiPromptImageGeneration: "",
};

// What the screen shows for one channel's connection.
//
// Three states, not two. "Connected" and "not connected" would hide the case
// that actually bites: a token that exists but has run out, which looks fine
// until a post silently fails. An expiry inside seven days gets its own
// warning for the same reason - Meta's long-lived tokens last 60 days and then
// simply stop.
const connectionState = (row) => {
  if (!Number(row.FlagConnected)) {
    return {
      key: "none",
      label: "Not connected",
      colour: "default",
      detail: row.OAuthLastError || null,
    };
  }

  const expiry = row.OAuthTokenExpiryDateTime
    ? new Date(String(row.OAuthTokenExpiryDateTime).replace(" ", "T"))
    : null;

  if (expiry && expiry.getTime() < Date.now()) {
    return {
      key: "expired",
      label: "Expired",
      colour: "error",
      detail: "The token ran out on " + row.OAuthTokenExpiryDateTime + ". Reconnect to carry on.",
    };
  }

  if (expiry) {
    const days = Math.ceil((expiry.getTime() - Date.now()) / 86400000);
    if (days <= 7) {
      return {
        key: "expiring",
        label: "Expires in " + days + (days === 1 ? " day" : " days"),
        colour: "warning",
        detail: "Reconnect before " + row.OAuthTokenExpiryDateTime + ".",
      };
    }
  }

  return {
    key: "connected",
    label: "Connected",
    colour: "success",
    detail: expiry ? "Valid until " + row.OAuthTokenExpiryDateTime : null,
  };
};

const ChannelsTab = () => {
  const dispatch = useDispatch();

  const {
    profileList,
    profileListLoading,
    providerList,
    createSuccess,
    updateSuccess,
    deleteSuccess,
    createLoading,
    updateLoading,
    connectStartSuccess,
    connectStartLoading,
    connectDisconnectSuccess,
  } = useSelector((state) => ({
    profileList:
      state.socialMediaManagementReducer.masterSocialMediaProfileList
        ?.socialmediaprofile_list,
    profileListLoading:
      state.socialMediaManagementReducer.masterSocialMediaProfileListLoading,
    providerList:
      state.socialMediaManagementReducer.masterSocialMediaProviderList
        ?.mastersocialmediaprovider_list,
    createSuccess:
      state.socialMediaManagementReducer.masterSocialMediaProfileCreateSuccess,
    updateSuccess:
      state.socialMediaManagementReducer.masterSocialMediaProfileUpdateSuccess,
    deleteSuccess:
      state.socialMediaManagementReducer.masterSocialMediaProfileDeleteSuccess,
    createLoading:
      state.socialMediaManagementReducer.masterSocialMediaProfileCreateLoading,
    updateLoading:
      state.socialMediaManagementReducer.masterSocialMediaProfileUpdateLoading,
    connectStartSuccess:
      state.socialMediaManagementReducer.connectStartSuccess,
    connectStartLoading:
      state.socialMediaManagementReducer.connectStartLoading,
    connectDisconnectSuccess:
      state.socialMediaManagementReducer.connectDisconnectSuccess,
  }));

  useEffect(() => {
    dispatch(fetchMasterSocialMediaProfileList());
    dispatch(fetchMasterSocialMediaProviderList());
  }, [dispatch]);

  const providers = useMemo(
    () => (Array.isArray(providerList) ? providerList : []),
    [providerList]
  );
  const channels = useMemo(
    () => (Array.isArray(profileList) ? profileList : []),
    [profileList]
  );

  const [editing, setEditing] = useState(null); // null = drawer closed
  const [deleteId, setDeleteId] = useState(null);

  const isEdit = Boolean(editing?.Id);
  const saving = createLoading || updateLoading;

  const openNew = () => setEditing({ ...EMPTY_CHANNEL });
  const openEdit = (row) =>
    setEditing({
      Id: row.Id,
      MasterSocialMediaProvider_Id: row.MasterSocialMediaProvider_Id ?? "",
      HandleName: row.HandleName ?? "",
      UrlProfileView: row.UrlProfileView ?? "",
      Notes: row.Notes ?? "",
      AiPromptContentGeneration: row.AiPromptContentGeneration ?? "",
      AiPromptImageGeneration: row.AiPromptImageGeneration ?? "",
    });

  const setField = (key, value) =>
    setEditing((prev) => ({ ...prev, [key]: value }));

  const canSave = Boolean(editing?.MasterSocialMediaProvider_Id);

  const handleSave = () => {
    if (!canSave) return;
    const body = {
      MasterSocialMediaProvider_Id: editing.MasterSocialMediaProvider_Id,
      HandleName: editing.HandleName || null,
      UrlProfileView: editing.UrlProfileView || null,
      Notes: editing.Notes || null,
      AiPromptContentGeneration: editing.AiPromptContentGeneration || null,
      AiPromptImageGeneration: editing.AiPromptImageGeneration || null,
    };
    if (isEdit) {
      dispatch(updateMasterSocialMediaProfile([{ Id: editing.Id, ...body }]));
    } else {
      dispatch(createMasterSocialMediaProfile([body]));
    }
  };

  // Which channel we last pressed Connect on. Needed because the response
  // carries a URL but not the row, and a spinner on every button would be a
  // lie about which one is working.
  const [connectingId, setConnectingId] = useState(null);
  const [disconnectId, setDisconnectId] = useState(null);
  // Undoing a disconnect means walking the platform's whole consent flow
  // again, so it gets the same confirm a delete does.
  const [confirmDisconnect, setConfirmDisconnect] = useState(null);

  // Send the browser to the platform.
  //
  // Same tab, not a popup: the platforms' consent screens frequently want a
  // re-login and 2FA, and popup blockers plus Meta's own framing rules make a
  // popup the less reliable of the two. The operator comes back to
  // /social-media-connect, which lands them here again when it is done.
  useEffect(() => {
    if (isEmpty(connectStartSuccess)) return;

    if (connectStartSuccess.status === 200 && connectStartSuccess.url_auth) {
      window.location.assign(connectStartSuccess.url_auth);
      // No state reset - we are leaving the page.
      return;
    }

    // The likely reason is that CRMit has no app credentials for this platform
    // yet. The backend says so in as many words; repeat it rather than
    // flattening it to "failed".
    toast.error(
      connectStartSuccess.message || "That connection could not be started."
    );
    setConnectingId(null);
    dispatch(socialMediaConnectStartSuccess([]));
  }, [connectStartSuccess, dispatch]);

  useEffect(() => {
    if (isEmpty(connectDisconnectSuccess)) return;
    if (connectDisconnectSuccess.status === 200) {
      toast.success("Disconnected");
      dispatch(fetchMasterSocialMediaProfileList());
    } else {
      toast.error(connectDisconnectSuccess.message || "Could not disconnect.");
    }
    setDisconnectId(null);
    dispatch(socialMediaConnectDisconnectSuccess([]));
  }, [connectDisconnectSuccess, dispatch]);

  // One success handler per verb. They all do the same three things (toast,
  // reload, clear the flag) but they are separate reducer keys, so separate
  // effects - collapsing them would mean watching three values and guessing
  // which one fired.
  useEffect(() => {
    if (isEmpty(createSuccess)) return;
    if (createSuccess.status === 200) {
      toast.success("Channel added");
      dispatch(fetchMasterSocialMediaProfileList());
      setEditing(null);
    } else {
      toast.error(createSuccess.message || "Could not add the channel");
    }
    dispatch(createMasterSocialMediaProfileSuccess([]));
  }, [createSuccess, dispatch]);

  useEffect(() => {
    if (isEmpty(updateSuccess)) return;
    if (updateSuccess.status === 200) {
      toast.success("Channel saved");
      dispatch(fetchMasterSocialMediaProfileList());
      setEditing(null);
    } else {
      toast.error(updateSuccess.message || "Could not save the channel");
    }
    dispatch(updateMasterSocialMediaProfileSuccess([]));
  }, [updateSuccess, dispatch]);

  useEffect(() => {
    if (isEmpty(deleteSuccess)) return;
    if (deleteSuccess.status === 200) {
      toast.success("Channel removed");
      dispatch(fetchMasterSocialMediaProfileList());
    } else {
      toast.error(deleteSuccess.message || "Could not remove the channel");
    }
    setDeleteId(null);
    dispatch(deleteMasterSocialMediaProfileSuccess([]));
  }, [deleteSuccess, dispatch]);

  return (
    <>
      <DeleteModal
        show={Boolean(confirmDisconnect)}
        customText={
          confirmDisconnect
            ? "This channel will stop being connected to " +
              (confirmDisconnect.ExternalAccountName ||
                confirmDisconnect.MasterSocialMediaProvider_Name ||
                "the platform") +
              ". The channel and its settings are kept - but reconnecting means signing in on the platform again."
            : ""
        }
        onDeleteClick={() => {
          setDisconnectId(confirmDisconnect.Id);
          dispatch(
            socialMediaConnectDisconnect([
              { SocialMediaProfile_Id: confirmDisconnect.Id },
            ])
          );
          setConfirmDisconnect(null);
        }}
        onCloseClick={() => setConfirmDisconnect(null)}
      />

      <DeleteModal
        show={Boolean(deleteId)}
        onDeleteClick={() =>
          // The endpoint iterates a bare array of ids, not objects.
          dispatch(deleteMasterSocialMediaProfile([deleteId]))
        }
        onCloseClick={() => setDeleteId(null)}
      />

      {/* Connected is not the same as publishing, and the gap between the two
          is exactly where a user would otherwise be misled. Said once, here,
          rather than hedged on every button. */}
      <Alert severity="info" sx={{ mb: 3 }}>
        <AlertTitle sx={{ fontWeight: 700 }}>
          What connecting does, and what it does not
        </AlertTitle>
        Connecting authorises CRMit with the platform and stores the account
        this channel posts as. That is what lets us know the account is real and
        reachable.
        <br />
        Automatic publishing is not switched on yet — posts you schedule are
        drafted and planned in CRMit, and still go out by hand. A connected
        channel is ready for it the day it lands.
      </Alert>

      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{ mb: 2 }}
      >
        <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
          Channels{" "}
          <Chip
            size="small"
            label={channels.length}
            sx={{ ml: 0.5, fontWeight: 600 }}
          />
        </Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openNew}>
          Add Channel
        </Button>
      </Stack>

      <div className="table-responsive">
        <table className="table table-hover align-middle mb-0">
          <thead className="table-light">
            <tr>
              <th style={{ width: 180 }}>Platform</th>
              <th style={{ width: 180 }}>Handle</th>
              <th style={{ width: 220 }}>Connection</th>
              <th>Profile URL</th>
              <th style={{ width: 130 }}>AI instructions</th>
              <th style={{ width: 150 }} className="text-end">
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {channels.length === 0 ? (
              <tr>
                <td colSpan={6} className="text-center text-muted py-4">
                  {profileListLoading
                    ? "Loading channels…"
                    : "No channels yet. Add one to start scheduling posts."}
                </td>
              </tr>
            ) : (
              channels.map((row) => {
                // Two AI prompts, either of which may be set - show which,
                // because "has instructions" and "has image instructions" are
                // different things to be looking for.
                const hasText = Boolean(row.AiPromptContentGeneration);
                const hasImage = Boolean(row.AiPromptImageGeneration);
                const conn = connectionState(row);
                const connected = conn.key !== "none";
                return (
                  <tr key={row.Id}>
                    <td>
                      <Stack
                        direction="row"
                        alignItems="center"
                        spacing={1}
                        useFlexGap
                      >
                        <i
                          className={
                            row.MasterSocialMediaProvider_Icon ||
                            "fa-regular fa-paper-plane"
                          }
                        />
                        <span className="fw-semibold">
                          {row.MasterSocialMediaProvider_Name || "—"}
                        </span>
                      </Stack>
                    </td>
                    <td>{row.HandleName || <span className="text-muted">—</span>}</td>
                    <td>
                      <Stack spacing={0.25}>
                        <Stack direction="row" spacing={0.75} alignItems="center">
                          {conn.key === "connected" ? (
                            <CheckCircleIcon
                              color="success"
                              sx={{ fontSize: 16 }}
                            />
                          ) : null}
                          {conn.key === "expired" || conn.key === "expiring" ? (
                            <ErrorOutlineIcon
                              color={conn.colour}
                              sx={{ fontSize: 16 }}
                            />
                          ) : null}
                          <Chip
                            size="small"
                            label={conn.label}
                            color={conn.colour}
                            variant={conn.key === "none" ? "outlined" : "filled"}
                          />
                        </Stack>
                        {/* The account, not just "connected" - this is how you
                            find out you authorised the wrong login. */}
                        {row.ExternalAccountName ? (
                          <Typography variant="caption" color="text.secondary">
                            {row.ExternalAccountName}
                          </Typography>
                        ) : null}
                        {conn.detail ? (
                          <Typography
                            variant="caption"
                            color={
                              conn.key === "none"
                                ? "error.main"
                                : "text.secondary"
                            }
                          >
                            {conn.detail}
                          </Typography>
                        ) : null}
                      </Stack>
                    </td>
                    <td>
                      {row.UrlProfileView ? (
                        <a
                          href={row.UrlProfileView}
                          target="_blank"
                          rel="noreferrer"
                        >
                          {row.UrlProfileView}{" "}
                          <OpenInNewIcon sx={{ fontSize: 14 }} />
                        </a>
                      ) : (
                        <span className="text-muted">—</span>
                      )}
                    </td>
                    <td>
                      {hasText || hasImage ? (
                        <Stack direction="row" spacing={0.5} useFlexGap>
                          {hasText ? (
                            <Chip size="small" label="Text" color="primary" />
                          ) : null}
                          {hasImage ? (
                            <Chip size="small" label="Image" color="primary" />
                          ) : null}
                        </Stack>
                      ) : (
                        <span className="text-muted">Defaults</span>
                      )}
                    </td>
                    <td className="text-end">
                      <Tooltip
                        title={
                          connected
                            ? "Reconnect (re-authorise, or switch account)"
                            : "Connect this channel to its platform"
                        }
                      >
                        <span>
                          <IconButton
                            size="small"
                            color={connected ? "default" : "primary"}
                            disabled={connectStartLoading}
                            onClick={() => {
                              setConnectingId(row.Id);
                              dispatch(socialMediaConnectStart(row.Id));
                            }}
                          >
                            {connectingId === row.Id && connectStartLoading ? (
                              <CircularProgress size={16} />
                            ) : (
                              <LinkIcon fontSize="small" />
                            )}
                          </IconButton>
                        </span>
                      </Tooltip>
                      {connected ? (
                        <Tooltip title="Disconnect">
                          <span>
                            <IconButton
                              size="small"
                              color="warning"
                              disabled={disconnectId === row.Id}
                              onClick={() => setConfirmDisconnect(row)}
                            >
                              <LinkOffIcon fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                      ) : null}
                      <Tooltip title="Edit channel">
                        <IconButton size="small" onClick={() => openEdit(row)}>
                          <EditIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Remove channel">
                        <IconButton
                          size="small"
                          color="error"
                          onClick={() => setDeleteId(row.Id)}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>
      </div>

      <Typography variant="caption" color="text.secondary" sx={{ mt: 2, display: "block" }}>
        Connecting opens the platform's own sign-in — CRMit never sees your
        password, and the token it receives stays on the server. Posts are
        written and scheduled on the{" "}
        <Link to="/social-media-management">Social Media Management</Link>{" "}
        calendar.
      </Typography>

      <AppModal
        open={Boolean(editing)}
        onClose={() => setEditing(null)}
        title={isEdit ? "Edit Channel" : "Add Channel"}
        subtitle="Which account you post to, and how AI should write for it"
        size="md"
        loading={saving}
        onSubmit={handleSave}
        submitLabel={isEdit ? "Save" : "Add Channel"}
        submitDisabled={!canSave || saving}
      >
        <Stack spacing={2.5} sx={{ pt: 1 }}>
          <TextField
            select
            required
            fullWidth
            size="small"
            label="Platform"
            value={editing?.MasterSocialMediaProvider_Id ?? ""}
            onChange={(e) =>
              setField("MasterSocialMediaProvider_Id", e.target.value)
            }
            helperText="Drives the icon, the preview and the built-in AI guidance for the platform"
          >
            {providers.map((p) => (
              <MenuItem key={p.Id} value={p.Id}>
                {p.Name}
              </MenuItem>
            ))}
          </TextField>

          <TextField
            fullWidth
            size="small"
            label="Handle"
            placeholder="@escituk"
            value={editing?.HandleName ?? ""}
            onChange={(e) => setField("HandleName", e.target.value)}
          />

          <TextField
            fullWidth
            size="small"
            label="Profile URL"
            placeholder="https://www.instagram.com/escituk"
            value={editing?.UrlProfileView ?? ""}
            onChange={(e) => setField("UrlProfileView", e.target.value)}
          />

          <TextField
            fullWidth
            multiline
            minRows={2}
            size="small"
            label="Notes"
            value={editing?.Notes ?? ""}
            onChange={(e) => setField("Notes", e.target.value)}
          />

          {/* Task 24875. These two are appended to the built-in platform
              guidance server side, not swapped for it - so a company writing
              one sentence here does not have to re-state how the platform
              reads. Both are optional; empty means "just the defaults". */}
          <Box>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5 }}>
              AI instructions for this channel
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Added to CRMit's own platform guidance whenever AI drafts a post
              or generates an image for this account. Leave blank to use the
              defaults.
            </Typography>
          </Box>

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Text generation"
            placeholder="e.g. Always finish with 'Find us on Instagram @escituk'."
            value={editing?.AiPromptContentGeneration ?? ""}
            onChange={(e) =>
              setField("AiPromptContentGeneration", e.target.value)
            }
          />

          <TextField
            fullWidth
            multiline
            minRows={3}
            size="small"
            label="Image generation"
            placeholder="e.g. Use our brand navy and orange. Never put text on the image."
            value={editing?.AiPromptImageGeneration ?? ""}
            onChange={(e) => setField("AiPromptImageGeneration", e.target.value)}
          />
        </Stack>
      </AppModal>
    </>
  );
};

export default ChannelsTab;
